/**
 * firmaAvanzata.js — la firma grafometrica avanzata (FEA, Namirial) vista
 * dalla pagina: chiede al gestionale di preparare il PDF, lo fa firmare sulla
 * tavoletta dal servizio locale FirmaCerta del PC, e riporta il firmato al
 * gestionale con la sessione dell'utente.
 *
 * SPENTA DI SERIE. La usa solo chi legge `avanzata: true` da
 * GET /template/firma-avanzata/stato, cioe' con FIRMA_CONSENSI_PROVIDER=namirial
 * dichiarato su una macchina di prova (backend/services/firma/provider.js).
 * Con il provider «interno» questo file non fa NIENTE oltre alla lettura dello
 * stato: nessuno script del servizio viene caricato, nessuna chiamata a
 * 127.0.0.1:7777.
 *
 * IL SERVIZIO LOCALE. Dall'installer FirmaCerta_SDK_WEB parte fchttpserver sul
 * PC della firma, porta 7777, SOLO https. La pagina carica
 * https://127.0.0.1:7777/files/fcsign.js, che espone window.fcsign; le
 * chiamate sono richieste <script> in stile JSONP e la risposta arriva a
 * UN SOLO callback globale (fcsign.callback): un'operazione alla volta, con un
 * tempo massimo. Cosi' l'ha vista la pagina di prova sul PC di Alex il 16/10
 * (prova-firma.js v5); la sequenza qui sotto e' la stessa.
 *
 * COSA E' SICURO E COSA E' SUPPOSTO (da riguardare con la Wacom, 19/10):
 *   sicuro   fcsign.js e la forma di window.fcsign; i metodi
 *            getSupportedTablets(soloCollegate), tabletStatusById(vendor,
 *            product), loadDocumentInMemory(url), isDocumentInMemory(),
 *            sign(file, nome, uploadUrl, nomeFirmato, 0),
 *            readSignedDocumentArray() -> { content: [byte] }, clearMemory();
 *            i codici 1000 («File da processare non trovato») e 118
 *            («Certificato per firma grafometrica non trovato»); i parametri
 *            di sign in fcsign.extraParams separati da ';'; dal 19/10 (STU-540)
 *            il codice 16 «Non ci sono firme da effettuare», uguale per il
 *            campo mancante e per quello gia' firmato (controllaFirma), e
 *            readSignedDocumentArray vuoto dopo una firma con OverwriteFile=1
 *            su file.
 *   uploadUrl, il terzo argomento di sign(), e' SEMPRE vuoto (decisione di Alex del
 *            22/10/2026: niente modo «file»).
 *
 * LA FIRMA IN MEMORIA (Namirial, risposta al ticket del 26/10/2026; decisione di Alex
 * dello stesso giorno, punto 1: «loadDocumentInMemory + sign("MEMORY") + lettura dalla
 * memoria; mai percorsi su disco. clearMemory subito dopo»). La sequenza e' UNA:
 *   loadDocumentInMemory(indirizzo del gestionale)  il servizio scarica il PDF una volta;
 *   sign("MEMORY", nome, '', nome firmato, 0)        firma il documento in memoria. Con un
 *            percorso o un indirizzo al posto di "MEMORY" il servizio firmerebbe un FILE sul
 *            disco del PC e la memoria resterebbe senza firmato (19/10, e Namirial): per
 *            questo la variante «indirizzo» di prima non c'e' piu';
 *   readSignedDocumentArray()                         i byte del firmato;
 *   clearMemory()                                     SUBITO, in un finally: anche se la
 *            lettura o la firma falliscono. Il servizio risponde a qualunque pagina aperta
 *            sul PC, e Namirial dice che clearMemory svuota (documento, firmato, campioni...)
 *            ma non garantisce la cancellazione sicura dei buffer: si tiene il meno possibile.
 * SULLA MACCHINA DI PROVA, e solo se il server lo offre (`caricamento_prova` nella
 * risposta della «prepara»: rotta montata con MACCHINA_DI_PROVA=1), fra la firma e la
 * lettura c'e' anche uploadSignedDocumentFromMemory verso la rotta di prova, che non
 * archivia niente: serve ad Alex per scegliere fra la lettura dalla pagina e il caricamento
 * diretto (decisione del 26/10, punto 6). La misura dei byte (misuraFirmato) resta a
 * schermo finche' non la si chiude.
 *
 * I MESSAGGI (decisione di Alex del 26/10, punto 4): Namirial non distingue i casi
 * (E16-E19: indirizzo non raggiungibile, download non riuscito, nessun firmato in memoria;
 * il 16 per campo assente, nome sbagliato, gia' firmato). A chi firma va UN messaggio solo
 * (MSG_FIRMA_NON_RIUSCITA, col codice del servizio quando c'e'); l'esito tecnico resta
 * nell'errore (`esito`) e il controllo vero e' quello del server sul PDF che torna.
 *
 * LA CONFIGURAZIONE DELLA POSTAZIONE sta in localStorage
 * (rimedichub_firma_sdk, JSON): `base` (di serie https://127.0.0.1:7777; si
 * accetta solo 127.0.0.1 o localhost, http o https). Serve alle prove (il servizio
 * finto sta su un'altra porta). Non decide niente di fiscale: i parametri della firma,
 * motivo, luogo e la posizione del campo li compone il server e li rilegge dal PDF firmato.
 *
 * NEL REFERTO O NEL LOG: mai l'indirizzo del PDF (ha un codice), mai i byte
 * del firmato, mai il nome del paziente. Un errore porta il codice del
 * servizio e una frase fissa.
 */
import { useEffect, useState } from 'react';
import api from './api';
import { useNotifiche } from '../utils/notifiche';

export const SDK_BASE_DI_SERIE = 'https://127.0.0.1:7777';
const CHIAVE_CONFIGURAZIONE = 'rimedichub_firma_sdk';
const ATTESA_MS = { breve: 20000, firma: 300000, svuota: 5000 };
/* Il primo argomento di sign() per firmare il documento caricato in memoria (Namirial, 26/10/2026). */
export const MEMORIA = 'MEMORY';
/* Il messaggio unico per chi firma (decisione di Alex del 26/10/2026, punto 4). */
export const MSG_FIRMA_NON_RIUSCITA = 'La firma con la tavoletta non è riuscita e il consenso non è archiviato. Riprova; se si ripete, avvisa l\'amministrazione.';

/* ---------------------------------------------------------------- */

/** La configurazione della postazione; ogni valore non valido torna al valore di serie. */
export function configurazione() {
    const c = { base: SDK_BASE_DI_SERIE };
    let letto = null;
    try { letto = JSON.parse(window.localStorage.getItem(CHIAVE_CONFIGURAZIONE) || 'null'); } catch (e) { letto = null; }
    if (letto && typeof letto === 'object') {
        try {
            const u = new URL(String(letto.base));
            if (['https:', 'http:'].includes(u.protocol) && ['127.0.0.1', 'localhost'].includes(u.hostname)) {
                c.base = `${u.protocol}//${u.host}`;
            }
        } catch (e) { /* resta quella di serie */ }
    }
    return c;
}

/* ---------------------------------------------------------------- */
/* Lo stato: chi firma i consensi, chiesto una volta per pagina.      */

let statoInCorso = null;
let statoNoto = null;

/** { avanzata, provider, configurata }: mai un'eccezione, e un errore di rete non si ricorda. */
export function leggiStatoFirma() {
    if (statoNoto) return Promise.resolve(statoNoto);
    if (!statoInCorso) {
        statoInCorso = api.get('/template/firma-avanzata/stato')
            .then((r) => {
                const d = (r && r.data && r.data.data) || {};
                statoNoto = { avanzata: d.avanzata === true, provider: String(d.provider || 'interno'), configurata: d.configurata !== false };
                return statoNoto;
            })
            .catch(() => ({ avanzata: false, provider: 'interno', configurata: false, errore: true }))
            .finally(() => { statoInCorso = null; });
    }
    return statoInCorso;
}

/** Per le prove: dimentica lo stato noto. */
export function dimenticaStatoFirma() { statoNoto = null; statoInCorso = null; }

/**
 * null finche' non lo si sa; poi { avanzata, ... }. Chi lo usa aspetta di
 * saperlo prima di disegnare la firma: cosi' la schermata non cambia sotto le
 * dita di chi ha gia' cominciato a disegnare.
 */
export function useFirmaAvanzata() {
    const [stato, setStato] = useState(statoNoto);
    useEffect(() => {
        if (statoNoto) { setStato(statoNoto); return undefined; }
        let attivo = true;
        leggiStatoFirma().then((s) => { if (attivo) setStato(s); });
        return () => { attivo = false; };
    }, []);
    return stato;
}

/* ---------------------------------------------------------------- */
/* Il servizio locale                                                 */

export class ErroreFirma extends Error {
    constructor(messaggio, codice) {
        super(messaggio);
        this.name = 'ErroreFirma';
        this.codiceServizio = codice === undefined ? null : codice;
    }
}

let sdkBase = null;

function caricaSdk(base) {
    if (sdkBase === base && typeof window.fcsign !== 'undefined') return Promise.resolve();
    return new Promise((ok, ko) => {
        const s = document.createElement('script');
        s.src = `${base}/files/fcsign.js`;
        s.onload = () => {
            if (typeof window.fcsign === 'undefined') {
                ko(new ErroreFirma('Il servizio di firma di questo computer ha risposto, ma non e\' quello atteso.'));
                return;
            }
            sdkBase = base;
            ok();
        };
        s.onerror = () => {
            s.remove();
            ko(new ErroreFirma('Il servizio di firma non risponde su questo computer. Controlla che FirmaCerta sia acceso, che il browser si fidi del suo certificato (apri una volta il suo indirizzo locale) e che questa pagina sia autorizzata a chiamarlo.'));
        };
        document.head.appendChild(s);
    });
}

/* fcsign ha UN callback globale: un'operazione alla volta. */
let inCorso = false;
function chiama(nome, args, attesa) {
    return new Promise((ok, ko) => {
        if (typeof window.fcsign === 'undefined') { ko(new ErroreFirma('Il servizio di firma non e\' caricato.')); return; }
        if (inCorso) { ko(new ErroreFirma('Un\'altra operazione di firma e\' in corso su questo computer.')); return; }
        inCorso = true;
        const t = setTimeout(() => {
            inCorso = false;
            ko(new ErroreFirma(`Il servizio di firma non ha risposto in ${Math.round(attesa / 1000)} secondi (${nome}).`));
        }, attesa);
        window.fcsign.callback = (dati) => { clearTimeout(t); inCorso = false; ok(dati || {}); };
        try {
            window.fcsign[nome].apply(window.fcsign, args);
        } catch (e) {
            clearTimeout(t); inCorso = false; ko(new ErroreFirma(`Il servizio di firma non accetta la richiesta (${nome}).`));
        }
    });
}

/**
 * L'esito di una risposta senza successo del servizio: { esito, frase }. Mai una riuscita.
 *
 * LA FRASE E' UNA (decisione di Alex del 26/10/2026, punto 4). Namirial ha confermato che
 * il servizio non distingue i casi: un indirizzo che non risponde (404, 403, rete) da'
 * «Errore di connessione http», un download non riuscito in sign() 1000, una lettura senza
 * firmato un messaggio vuoto, e il 16 vale per campo assente, nome sbagliato e campo gia'
 * firmato. A chi firma va quindi lo stesso messaggio, col codice del servizio quando c'e';
 * l'unica eccezione e' il 118 (manca il certificato sulla postazione), che non si risolve
 * riprovando. L'`esito` resta diverso, per chi guarda l'errore (la prova, il registro): un
 * INDIZIO dal testo del servizio, mai una diagnosi e mai una riuscita. Il controllo vero e'
 * quello del server sul PDF che torna (services/firma/ricezioneFea.js).
 * Nel messaggio non va mai il testo del servizio: a volte porta un indirizzo (col codice
 * monouso del PDF) o un percorso del PC.
 */
export function esitoDelServizio(r) {
    const codice = r && r.errorCode !== undefined && r.errorCode !== null && r.errorCode !== '' ? Number(r.errorCode) : null;
    const msg = r && r.errorMessage ? String(r.errorMessage).replace(/\s+/g, ' ') : '';
    const frase = codice !== null ? `${MSG_FIRMA_NON_RIUSCITA} (codice ${codice})` : MSG_FIRMA_NON_RIUSCITA;
    if (codice === 118) {
        return { esito: 'certificato_mancante', frase: 'Su questo computer manca il certificato per la firma grafometrica (codice 118): va installato una volta, con le istruzioni della prova.' };
    }
    let esito = 'non_riuscita';
    if (/errore di connessione http/i.test(msg)) esito = 'connessione';
    else if (codice === 1000 && /download/i.test(msg)) esito = 'download';
    else if (codice === 1000 && /non trovato/i.test(msg)) esito = 'documento_non_trovato';
    else if (codice === 1000 && /autorizzazion|modificare il file/i.test(msg)) esito = 'file_non_scritto';
    else if (codice === 1000) esito = '1000_non_riconosciuto';
    else if (codice === 16) esito = 'nessuna_firma_da_fare';
    else if (!msg && codice === null) esito = 'senza_messaggio';
    return { esito, frase };
}

/** La frase per un esito senza successo del servizio. Mai testo del documento, mai un indirizzo. */
export function fraseDelServizio(r) {
    return esitoDelServizio(r).frase;
}

function controlla(r) {
    if (!r || r.success !== true) {
        const { esito, frase } = esitoDelServizio(r);
        const e = new ErroreFirma(frase, r && r.errorCode);
        e.esito = esito;
        throw e;
    }
    return r;
}

/**
 * L'esito di sign(). Il CODICE 16 («Non ci sono firme da effettuare») non e' MAI una
 * riuscita, nemmeno se arrivasse con success vero. Namirial (26/10/2026): lo stesso 16 per
 * campo assente, nome sbagliato e campo gia' firmato. A schermo il messaggio unico; l'esito
 * tecnico e' quello che il server ha previsto per QUESTO documento nella «prepara»
 * (`se_codice_16`), se c'e'.
 */
export function controllaFirma(r, preparato) {
    if (r && Number(r.errorCode) === 16) {
        const esito = preparato && preparato.se_codice_16;
        const e = new ErroreFirma(`${MSG_FIRMA_NON_RIUSCITA} (codice 16)`, 16);
        e.esito = esito && esito.codice ? String(esito.codice) : 'nessuna_firma_da_fare';
        throw e;
    }
    return controlla(r);
}

const PDF_ALL_INIZIO = (byte) => byte && byte.length >= 8
    && String.fromCharCode(byte[0], byte[1], byte[2], byte[3], byte[4]) === '%PDF-';

/** Fra le tavolette, la STU-540 se c'e', altrimenti la prima. */
function sceltaTavoletta(elenco) {
    const lista = Array.isArray(elenco) ? elenco : [];
    return lista.find((t) => /540/.test(`${t.modelAsString} ${t.name}`)) || lista[0] || null;
}

/** I parametri della firma: quelli del server (ordine) piu' la tavoletta di questo PC. */
export function parametriDellaFirma(extraParams, tavoletta) {
    const lista = [String(extraParams || '')];
    if (tavoletta) {
        if (tavoletta.isTabletPC) lista.push('AdvancedSignature.FromTabletPC=1', `AdvancedSignature.TabletPCModel=${tavoletta.modelAsString}`);
        else lista.push('AdvancedSignature.FromTabletPC=0', `AdvancedSignature.TabletModel=${tavoletta.modelAsString}`);
    }
    return lista.filter(Boolean).join(';');
}

/* ---------------------------------------------------------------- */
/* La misura (solo sulla macchina di prova)                           */

const contaEof = (byte, da = 0) => {
    let n = 0;
    for (let i = da; i + 5 <= byte.length; i++) {
        if (byte[i] === 37 && byte[i + 1] === 37 && byte[i + 2] === 69 && byte[i + 3] === 79 && byte[i + 4] === 70) n++;
    }
    return n;
};

function base64InByte(b64) {
    try {
        const s = window.atob(String(b64 || ''));
        const u = new Uint8Array(s.length);
        for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
        return u;
    } catch (e) { return null; }
}

/** Il /Rect dell'ultima versione del campo di firma nel firmato, o null. Solo numeri. */
function rettangoloDelCampo(byte, nome) {
    let latin = '';
    for (let i = 0; i < byte.length; i += 8192) latin += String.fromCharCode.apply(null, byte.subarray(i, i + 8192));
    const i = latin.lastIndexOf(`/T (${nome})`);
    if (i < 0) return null;
    const inizio = latin.lastIndexOf(' obj', i);
    const fine = latin.indexOf('endobj', i);
    const m = /\/Rect\s*\[\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s*\]/.exec(latin.slice(inizio < 0 ? 0 : inizio, fine < 0 ? latin.length : fine));
    return m ? m.slice(1, 5).map((n) => Math.round(Number(n) * 100) / 100) : null;
}

/**
 * La misura del firmato contro il documento mandato: solo numeri e si/no, mai il contenuto.
 * E' quello che Namirial dice verificabile (aggiornamento incrementale: il firmato comincia
 * con i byte dell'originale) e che il server controlla (services/firma/byteIniziali.js).
 */
export function misuraFirmato(originale, firmato, campo) {
    const o = originale || new Uint8Array(0);
    const f = firmato || new Uint8Array(0);
    let uguali = o.length > 0 && f.length >= o.length;
    for (let i = 0; uguali && i < o.length; i++) if (o[i] !== f[i]) uguali = false;
    return {
        lunghezza_originale: o.length,
        lunghezza_firmato: f.length,
        inizio_identico: uguali,
        eof_originale: contaEof(o),
        eof_firmato: contaEof(f),
        rect_atteso: campo && campo.rect_atteso ? campo.rect_atteso : null,
        rect_trovato: f.length ? rettangoloDelCampo(f, 'firma_paziente') : null,
    };
}

let ultimaMisura = null;
/** L'ultima misura fatta in questa pagina (per copiarla), o null. */
export function misuraDellUltimaFirma() { return ultimaMisura; }

const siNo = (v) => (v ? 'sì' : 'no');
function testoMisura(m) {
    const r = [
        'Misura della firma (prova, nessun consenso in più se la ricezione la rifiuta):',
        `lettura dalla pagina: ${m.lettura ? `originale ${m.lettura.lunghezza_originale} byte, firmato ${m.lettura.lunghezza_firmato} byte, inizio identico ${siNo(m.lettura.inizio_identico)}, %%EOF ${m.lettura.eof_originale} → ${m.lettura.eof_firmato}, campo atteso [${(m.lettura.rect_atteso || []).join(' ')}] trovato [${(m.lettura.rect_trovato || []).join(' ')}]` : `non riuscita (${m.lettura_esito || 'nessun esito'})`}`,
        `caricamento diretto: ${m.caricamento ? `servizio ${m.caricamento.success ? 'riuscito' : 'non riuscito'}${m.caricamento.codice !== null && m.caricamento.codice !== undefined ? ` (codice ${m.caricamento.codice})` : ''}` : 'non provato'}`,
    ];
    if (m.server) {
        const x = m.server.misura || {};
        r.push(`server: ${m.server.esito || m.server.codice || 'nessun esito'}${x.lunghezza_firmato !== undefined ? `, firmato ${x.lunghezza_firmato} byte, inizio identico ${siNo(x.inizio_identico)}, %%EOF ${x.eof_nel_firmato}, forma ${m.server.forma || '?'}` : ''}`);
    }
    return r.join('\n');
}

/** Mostra la misura finche' non la si chiude, e la tiene per copiarla. Non solleva mai. */
function riferisciMisura(m) {
    ultimaMisura = m;
    try { window.__rimedichubMisuraFea = m; } catch (e) { /* niente */ }
    try { window.sessionStorage.setItem('rimedichub_misura_fea', JSON.stringify(m)); } catch (e) { /* memoria del browser non disponibile */ }
    try { useNotifiche.getState().aggiungi({ id: `misura-fea-${Date.now()}`, tipo: 'attenzione', testo: testoMisura(m) }); } catch (e) { /* niente */ }
}

/** Il gettone dal percorso della rotta di prova (parametro t), per chiedere l'esito al server. */
const gettoneDi = (percorso) => {
    try { return new URL(percorso, window.location.origin).searchParams.get('t') || ''; } catch (e) { return ''; }
};

/**
 * Fa firmare il PDF preparato e torna con i byte del firmato.
 *
 * @param {object} preparato  la risposta di POST /template/firma-avanzata/prepara
 *                            (extraParams, pdf_percorso, nome_file; sulla macchina di prova
 *                            anche caricamento_prova)
 * @param {(passo: string) => void} [alPasso]  'tavoletta' | 'documento' | 'firma' | 'caricamento' | 'lettura'
 * @returns {Promise<Uint8Array>}
 * @throws {ErroreFirma} con una frase per chi lavora
 */
export async function firmaSullaTavoletta(preparato, alPasso = () => {}) {
    const cfg = configurazione();
    await caricaSdk(cfg.base);
    const prova = preparato && preparato.caricamento_prova && preparato.caricamento_prova.percorso ? preparato.caricamento_prova : null;
    const misura = prova ? { caricamento: null, lettura: null, server: null } : null;
    let svuotare = false;
    try {
        alPasso('tavoletta');
        const elenco = controlla(await chiama('getSupportedTablets', [true], ATTESA_MS.breve));
        const tavoletta = sceltaTavoletta(elenco.tablets);
        if (!tavoletta) throw new ErroreFirma('Nessuna tavoletta collegata a questo computer.');
        const stato = await chiama('tabletStatusById', [tavoletta.vendorId, tavoletta.productId], ATTESA_MS.breve);
        if (stato && stato.success === true && stato.successMessage && stato.successMessage !== 'Connected') {
            throw new ErroreFirma(`La tavoletta non risulta collegata (${String(stato.successMessage).slice(0, 40)}).`);
        }

        alPasso('documento');
        const indirizzo = `${window.location.origin}${preparato.pdf_percorso}`;
        window.fcsign.extraParams = parametriDellaFirma(preparato.extraParams, tavoletta);
        svuotare = true;

        controlla(await chiama('loadDocumentInMemory', [indirizzo], ATTESA_MS.breve));
        const inMemoria = await chiama('isDocumentInMemory', [], ATTESA_MS.breve);
        if (!inMemoria || inMemoria.success !== true) {
            const e = new ErroreFirma(MSG_FIRMA_NON_RIUSCITA);
            e.esito = 'non_in_memoria';
            throw e;
        }

        alPasso('firma');
        /* "MEMORY": si firma il documento in memoria, mai un file sul disco del PC. uploadUrl
           SEMPRE vuoto (decisione di Alex del 22/10/2026). */
        controllaFirma(await chiama('sign', [MEMORIA, preparato.nome_file || 'consenso.pdf', '', 'consenso-firmato.pdf', 0], ATTESA_MS.firma), preparato);

        if (prova) {
            /* Il caricamento diretto, solo sulla macchina di prova: la rotta non archivia niente. */
            alPasso('caricamento');
            try {
                const c = await chiama('uploadSignedDocumentFromMemory', [`${window.location.origin}${prova.percorso}`, 'firmato.pdf', false], ATTESA_MS.breve);
                misura.caricamento = { success: c.success === true, codice: c.errorCode === undefined ? null : c.errorCode };
            } catch (e) {
                misura.caricamento = { success: false, codice: null, errore: 'nessuna risposta' };
            }
        }

        alPasso('lettura');
        const letto = controlla(await chiama('readSignedDocumentArray', [], ATTESA_MS.breve));
        if (!Array.isArray(letto.content) || letto.content.length < 8) {
            const e = new ErroreFirma(MSG_FIRMA_NON_RIUSCITA);
            e.esito = 'firmato_vuoto';
            throw e;
        }
        const byte = Uint8Array.from(letto.content);
        if (misura) misura.lettura = misuraFirmato(base64InByte(preparato.pdf_base64), byte, preparato.campo_posizione);
        if (!PDF_ALL_INIZIO(byte)) {
            const e = new ErroreFirma(MSG_FIRMA_NON_RIUSCITA);
            e.esito = 'firmato_non_pdf';
            throw e;
        }
        return byte;
    } catch (e) {
        if (misura) misura.lettura_esito = e && e.esito ? String(e.esito) : 'errore';
        throw e;
    } finally {
        /* Il documento firmato e i campioni restano nella memoria del servizio,
           che risponde a qualunque pagina aperta sul PC: si svuota SUBITO, anche
           dopo un errore. Se non risponde, non cambia l'esito della firma. */
        if (svuotare) {
            try { await chiama('clearMemory', [], ATTESA_MS.svuota); } catch (e) { /* la prova dice se e' rimasta piena */ }
        }
        if (misura) {
            if (misura.caricamento) {
                try {
                    const r = await api.post('/template/firma-avanzata/prova-caricamento/esito', { token: gettoneDi(prova.percorso) });
                    misura.server = r.data && r.data.data ? r.data.data : null;
                } catch (e) {
                    misura.server = { codice: (e && e.response && e.response.data && e.response.data.codice) || 'esito non disponibile' };
                }
            }
            riferisciMisura(misura);
        }
    }
}

/* ---------------------------------------------------------------- */
/* Il gestionale                                                      */

/** Chiede al server il PDF col campo di firma vuoto e l'ordine. */
export async function preparaConsensoFea(corpo) {
    const r = await api.post('/template/firma-avanzata/prepara', corpo);
    return r.data.data;
}

/** Porta al server il firmato; il server lo controlla e lo archivia. Torna il documento. */
export async function inviaFirmato(ordine, byte) {
    const fd = new FormData();
    fd.append('ordine', ordine);
    fd.append('firmato', new Blob([byte], { type: 'application/pdf' }), 'firmato.pdf');
    const r = await api.post('/template/firma-avanzata/ricevi', fd, { headers: { 'Content-Type': undefined } });
    return r.data.data.documento;
}

/**
 * Tutto il giro per un consenso: prepara, firma sulla tavoletta, archivia.
 * @param {object} corpo  template_id, paziente_id, visita_id, medico_id, prestazione, risposte
 * @param {(passo: string) => void} [alPasso]  'prepara' | 'tavoletta' | 'documento' | 'firma' | 'lettura' | 'archivia'
 * @returns {Promise<object>} il documento archiviato (la stessa riga della firma a schermo)
 */
export async function firmaConsensoConTavoletta(corpo, alPasso = () => {}) {
    alPasso('prepara');
    const preparato = await preparaConsensoFea(corpo);
    const byte = await firmaSullaTavoletta(preparato, alPasso);
    alPasso('archivia');
    return inviaFirmato(preparato.ordine, byte);
}
